import React, { useEffect, useState } from "react";
import { Route, useRouteMatch } from "react-router-dom";
import { useAuth } from "../../context/auth-context";
import { ErrorBoundary } from "react-error-boundary";
import ErrorFallback from "../errorHandling/error-fallback-page";
import { handleBoundaryError } from '../../utils/globalErrorHandler';
import { authenticateAuth } from "../../helpers/jwtAuth";
import LoginRedirect from "./LoginRedirect";

/**
 * AuthorizedRoute - Route wrapper that verifies a specific authorization type via `authenticateAuth`.
 * Renders null while the async auth check is pending, then redirects to `/login` on failure.
 *
 * @param {Object} props
 * @param {string} props.authorizationType - The authorization type string checked against the JWT.
 * @param {React.ComponentType} props.component - The component to render when authorized.
 * @returns {JSX.Element}
 */
const AuthorizedRoute = ({
  authorizationType,
  component: Component,
  ...rest
}) => {
  //state
  const [authStatus, setAuthStatus] = useState(false);
  const { currentUser, accessToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const match = useRouteMatch({ path: rest.path, exact: rest.exact });
  //functions
  useEffect(() => {
    if (!match) {
      setLoading(false);
      return;
    }

    /**
     * asyncEffect - Performs the asynchronous authentication check using `authenticateAuth`.
     * Sets `authStatus` based on the result and updates `loading` state accordingly.
     * @param {string} authorizationType - The authorization type string checked against the JWT.
     * @param {string} accessToken - The JWT access token used for authentication.
     * @param {object} match - The match object from `useRouteMatch` to determine if the route is active.
     * @return {Promise<void>}
     */
    const asyncEffect = async () => {
      if (!accessToken) {
        setAuthStatus(false);
        setLoading(false);
        return;
      }
      try {
        const auth = await authenticateAuth(authorizationType);
        setAuthStatus(auth);
        setLoading(false);
      } catch (error) {
        setAuthStatus(false);
        setLoading(false);
      }
    };

    asyncEffect();
  }, [authorizationType, accessToken, match]);

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback} onError={handleBoundaryError}>
      <Route
        {...rest}
        render={(props) => {
          return !loading ? (
            <>
              {currentUser && authStatus ? (
                <Component {...props} />
              ) : (
                <LoginRedirect />
              )}
            </>
          ) : null;
        }}
      ></Route>
    </ErrorBoundary>
  );
}

export default AuthorizedRoute
